import React, { useMemo, useRef, useState } from "react"
import { Icon, GoogleMaps } from "components"
import Strings from "utils/strings"
import { Route } from "../types"
import { getRouteColor } from "../routeColors"
import { spreadOverlappingMarkers } from "../mapMarkerUtils"
import { MovePreviewState } from "./MoveOrdersTimeline/types"
import { changeTypeFor } from "./MoveOrdersTimeline/utils"

interface RouteSequenceMapProps {
	selectedRoutes: Route[]
	// Live drag state reported by MoveOrdersTimeline — stops are read from
	// `movePreview.stopsByRoute` (current membership) rather than each route's
	// own (stale) `addresses`, so a stop dragged into another route shows up
	// there immediately, with that route's color and its new sequence number.
	movePreview: MovePreviewState
}

// Same badge colors as Pill.tsx's corner badge — a Maps Symbol marker can't
// render a corner icon, so a changed stop gets a matching colored ring
// instead of the timeline's badge shape.
const CHANGE_STROKE_COLOR: Record<string, string> = {
	moved: "#d46b08",
	repositioned: "#1890ff",
	none: "white",
}

// Same rule as FleetTrackingMap/FleetDashboardMap's own `mapStyles.hidePOIs`.
const POI_HIDDEN_STYLE: google.maps.MapTypeStyle[] = [
	{ featureType: "poi", elementType: "all", stylers: [{ visibility: "off" }] },
	{
		featureType: "transit",
		elementType: "all",
		stylers: [{ visibility: "off" }],
	},
]

// Same numbered-marker map as MultiRoutePanel's, shown alongside the timeline
// so the person moving orders between routes can check the move makes
// geographic sense before committing it.
const RouteSequenceMap: React.FC<RouteSequenceMapProps> = ({
	selectedRoutes,
	movePreview,
}) => {
	const [mapZoom, setMapZoom] = useState(12)
	// Same convention as FleetTrackingMap/FleetDashboardMap: false = hidden.
	const [showPOIs, setShowPOIs] = useState(false)
	const {
		stopsByRoute,
		sequenceByRoute,
		pendingMoves,
		positionOverrides,
		selectedOrderId,
	} = movePreview

	// Stable color per route, assigned the first time it's seen and never
	// reassigned — matches MoveOrdersTimeline's own (independent) color
	// assignment over the same selectedRoutes order, so colors line up.
	const colorIndexRef = useRef<Map<string, number>>(new Map())
	selectedRoutes.forEach((route) => {
		if (!colorIndexRef.current.has(route._id)) {
			colorIndexRef.current.set(route._id, colorIndexRef.current.size)
		}
	})
	const routeColor = (routeId: string) =>
		getRouteColor(colorIndexRef.current.get(routeId) ?? 0)

	const stopMarkers = useMemo(
		() =>
			selectedRoutes.flatMap((route) => {
				const color = routeColor(route._id)
				const sequence = sequenceByRoute[route._id] || {}
				return (stopsByRoute[route._id] || [])
					.filter(
						(addr) => addr.location?.lat != null && addr.location?.lng != null
					)
					.map((addr) => {
						const orderId = addr.order.orderId
						const seq = sequence[orderId] ?? "?"
						const changeType = changeTypeFor(
							orderId,
							pendingMoves,
							positionOverrides
						)
						const selected = orderId === selectedOrderId
						return {
							lat: addr.location.lat,
							lng: addr.location.lng,
							label: {
								text: String(seq),
								color: "white",
								fontSize: "10px",
								fontWeight: "bold",
							},
							icon: {
								path: 0, // google.maps.SymbolPath.CIRCLE
								fillColor: color,
								fillOpacity: 1,
								scale: selected ? 13 : 9,
								strokeColor: selected
									? "#1a1d24"
									: CHANGE_STROKE_COLOR[changeType],
								strokeWeight: selected ? 4 : changeType === "none" ? 2 : 3,
							},
							overlay: (
								<div style={{ fontSize: 12, fontWeight: 600 }}>
									{route.name} · {Strings.routes.stopNumber} {seq}
								</div>
							),
						}
					})
			}),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[
			selectedRoutes,
			stopsByRoute,
			sequenceByRoute,
			pendingMoves,
			positionOverrides,
			selectedOrderId,
		]
	)

	// Depots are shared by multiple routes, so they get one marker per unique
	// location, colored like the (first) route that uses it.
	const depotMarkers = useMemo(() => {
		const seen = new Set<string>()
		const markers: any[] = []
		selectedRoutes.forEach((route) => {
			const color = routeColor(route._id)
			;(route.addresses || []).forEach((addr) => {
				if (
					!addr.isDepot ||
					addr.location?.lat == null ||
					addr.location?.lng == null
				)
					return
				const key = `${addr.location.lat.toFixed(5)},${addr.location.lng.toFixed(5)}`
				if (seen.has(key)) return
				seen.add(key)
				markers.push({
					lat: addr.location.lat,
					lng: addr.location.lng,
					label: {
						text: "D",
						color: "white",
						fontSize: "11px",
						fontWeight: "bold",
					},
					icon: {
						path: "M 0,-10 L 10,0 L 0,10 L -10,0 Z", // diamond
						fillColor: color,
						fillOpacity: 1,
						scale: 1,
						strokeColor: "white",
						strokeWeight: 2,
					},
				})
			})
		})
		return markers
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedRoutes])

	const allMarkers = useMemo(
		() => spreadOverlappingMarkers([...stopMarkers, ...depotMarkers], mapZoom),
		[stopMarkers, depotMarkers, mapZoom]
	)

	// Built straight from stop/depot locations, not from `stopMarkers`/
	// `depotMarkers` — those also carry selection/change-type styling (icon
	// color, scale), so bounding off them would recompute (and, via
	// refitOnBoundsChange, refit) whenever a pill gets selected or a stop's
	// change badge updates, fighting whatever zoom/pan the user already did.
	const boundingBox = useMemo(() => {
		const lats: number[] = []
		const lngs: number[] = []
		selectedRoutes.forEach((route) => {
			;(stopsByRoute[route._id] || []).forEach((addr) => {
				if (addr.location?.lat != null && addr.location?.lng != null) {
					lats.push(addr.location.lat)
					lngs.push(addr.location.lng)
				}
			})
			;(route.addresses || []).forEach((addr) => {
				if (
					addr.isDepot &&
					addr.location?.lat != null &&
					addr.location?.lng != null
				) {
					lats.push(addr.location.lat)
					lngs.push(addr.location.lng)
				}
			})
		})
		if (lats.length === 0) return undefined
		const pad = 0.003
		return {
			south: Math.min(...lats) - pad,
			north: Math.max(...lats) + pad,
			west: Math.min(...lngs) - pad,
			east: Math.max(...lngs) + pad,
		}
	}, [selectedRoutes, stopsByRoute])

	return (
		<div style={{ position: "relative", width: "100%", height: "100%" }}>
			<button
				type="button"
				onClick={() => setShowPOIs((prev) => !prev)}
				className={`RoutesScreen__poi-toggle${showPOIs ? " RoutesScreen__poi-toggle--active" : ""}`}
			>
				<Icon
					name={showPOIs ? "eye" : "eye-outline"}
					style={{
						fontSize: 14,
						color: showPOIs ? "white" : "rgba(0, 0, 0, 0.6)",
					}}
				/>
				<span>POIs</span>
			</button>
			<GoogleMaps
				boundingBox={boundingBox}
				refitOnBoundsChange
				markers={allMarkers}
				mapContainerStyle={{ width: "100%", height: "100%", borderRadius: 8 }}
				options={{
					gestureHandling: "cooperative",
					clickableIcons: false,
					styles: showPOIs ? undefined : POI_HIDDEN_STYLE,
				}}
				onMapLoad={(map) => {
					const updateZoom = () => setMapZoom(map.getZoom() ?? 12)
					// fitBounds() recalculates zoom asynchronously, so reading
					// getZoom() right away can still reflect the pre-fit zoom —
					// "idle" fires once the map has settled after the fit.
					google.maps.event.addListenerOnce(map, "idle", updateZoom)
					map.addListener("zoom_changed", updateZoom)
				}}
			/>
		</div>
	)
}

export default RouteSequenceMap
